/**
 * @module The source list of a Flux Agent web-search card. Titles and passages are untrusted
 * text from the public web, so they render only as React text nodes; links open in a new tab
 * with `rel="noopener noreferrer"` and only after {@link sourceLink} accepts the URL.
 */

import { ExternalLink } from 'lucide-react';
import type { FluxAgentSearchSource } from '@/lib/fluxAgentApi';
import { sourceLink } from '@/lib/fluxAgentSources';

/** Inputs for {@link FluxAgentSearchSources}. */
export interface FluxAgentSearchSourcesProps {
  /** Sources from the card, cited ones first; renders nothing when empty. */
  sources: FluxAgentSearchSource[];
}

/** List web sources as safe external links with their host and cited passage. */
export function FluxAgentSearchSources({ sources }: FluxAgentSearchSourcesProps) {
  if (!sources.length) return null;
  return (
    <ol className="mt-1.5 space-y-1" aria-label="Sources" data-testid="flux-agent-search-sources">
      {sources.map((source, index) => {
        const link = sourceLink(source.url);
        return (
          <li key={`${source.url}-${index}`} className="min-w-0">
            {link ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="text-primary inline-flex max-w-full items-center gap-1 font-medium underline-offset-2 hover:underline"
              >
                <span className="truncate">{source.title || link.host}</span>
                <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            ) : (
              <span className="font-medium">{source.title}</span>
            )}
            {link && <span className="text-muted-foreground ml-1">{link.host}</span>}
            {source.snippet && (
              <p className="text-muted-foreground line-clamp-2 break-words">{source.snippet}</p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
